import { WeatherSample } from './payload';

export interface Bucketed {
  starts: Date[];
  bucketMs: number;
  temperature: Array<number | null>;
  humidity: Array<number | null>;
  pressure: Array<number | null>;
  windSpeed: Array<number | null>;
  rain: Array<number | null>;
  battery: Array<number | null>;
}

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

/**
 * Bucket width per window: 24 h keeps the stations' 3-minute send rate (480 points); longer windows keep roughly
 * 80–90 points.
 */
export const bucketMsFor = (rangeMs: number): number => {
  if (rangeMs <= 24 * HOUR) return 3 * MINUTE;
  if (rangeMs <= 7 * 24 * HOUR) return 2 * HOUR;
  return 8 * HOUR;
};

/**
 * Rain per reading from the cumulative `rainfall_total`. A drop means the gauge was reset, in which case the new total
 * is all rain since the reset.
 */
export const rainIncrements = (samples: WeatherSample[]): Array<number | undefined> => {
  let previous: number | undefined;
  return samples.map((s) => {
    if (s.rainfallTotal === undefined) return undefined;
    const increment =
      previous === undefined ? 0 : s.rainfallTotal >= previous ? s.rainfallTotal - previous : s.rainfallTotal;
    previous = s.rainfallTotal;
    return increment;
  });
};

const mean = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
const sum = (values: number[]) => (values.length ? values.reduce((a, b) => a + b, 0) : null);
const last = (values: number[]) => (values.length ? values[values.length - 1] : null);

/**
 * Averages each measurement per bucket over [from, to); rain is summed and battery keeps the bucket's last reading.
 * Empty buckets are null (a gap).
 */
export const bucketize = (samples: WeatherSample[], from: Date, to: Date, bucketMs: number): Bucketed => {
  const count = Math.max(1, Math.ceil((to.getTime() - from.getTime()) / bucketMs));
  const fields = ['temperature', 'humidity', 'pressure', 'windSpeed'] as const;
  const groups = Array.from({ length: count }, () => ({
    temperature: [] as number[],
    humidity: [] as number[],
    pressure: [] as number[],
    windSpeed: [] as number[],
    rain: [] as number[],
    battery: [] as number[],
  }));
  const rain = rainIncrements(samples);
  samples.forEach((s, i) => {
    const index = Math.floor((s.time.getTime() - from.getTime()) / bucketMs);
    if (index < 0 || index >= count) return;
    const group = groups[index];
    fields.forEach((f) => {
      const v = s[f];
      if (v !== undefined) group[f].push(v);
    });
    if (rain[i] !== undefined) group.rain.push(rain[i] as number);
    if (s.battery !== undefined) group.battery.push(s.battery);
  });
  return {
    starts: groups.map((_, i) => new Date(from.getTime() + i * bucketMs)),
    bucketMs,
    temperature: groups.map((g) => mean(g.temperature)),
    humidity: groups.map((g) => mean(g.humidity)),
    pressure: groups.map((g) => mean(g.pressure)),
    windSpeed: groups.map((g) => mean(g.windSpeed)),
    rain: groups.map((g) => sum(g.rain)),
    battery: groups.map((g) => last(g.battery)),
  };
};

export const finite = (values: Array<number | null | undefined>): number[] =>
  values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
